import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, CircleDashed, Lock, LockOpen } from 'lucide-react'
import { closePeriodWithBalances, getMonthEnd, postJobsWip, postProductionOverhead, reopenPeriod, undoJobsWip, undoProductionOverhead } from '../../api/accounting'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { useToast } from '../../components/Toast.jsx'
import { fmtDateTime, fmtDay } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { useSession } from '../../session'
import { money } from '../sales/salesShared'
import AccountingTabs from './AccountingTabs'
import { StockValueForm } from '../purchase/StockValue'
import { monthName, recentMonths, thisYm } from './accountingShared'

// Month-end close: the checks to run on a month, in order, then closing it
// (or reopening it). Replaces Period Close, Subledger Tie-Outs and the
// Inventory Cutoff pages.
export default function MonthEnd() {
  const { notify } = useToast()
  const { has } = useSession()
  const [params, setParams] = useSearchParams()
  const months = useMemo(() => recentMonths(24), [])
  const asked = params.get('month')
  const [ym, setYm] = useState(/^\d{4}-\d{2}$/.test(asked || '') ? asked : null)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [dialog, setDialog] = useState(null)
  const [busy, setBusy] = useState(false)
  const [version, setVersion] = useState(0)

  // Without a month in the address, start on the oldest open month of the
  // last two (usually last month while it's still open), else this month.
  useEffect(() => {
    if (ym) return
    getMonthEnd(thisYm()).then((d) => {
      const closed = new Set((d.history || []).map((h) => h.period_ym))
      const last = months[1]
      setYm(!closed.has(last) ? last : months[0])
    }).catch(() => setYm(months[0]))
  }, [ym, months])

  useEffect(() => {
    if (!ym) return
    setLoading(true)
    setError('')
    getMonthEnd(ym)
      .then(setData)
      .catch((e) => setError(e.message || 'Could not load the month.'))
      .finally(() => setLoading(false))
  }, [ym, version])

  const choose = (value) => {
    setYm(value)
    setParams((p) => { const n = new URLSearchParams(p); n.set('month', value); return n }, { replace: true })
  }

  const run = async (fn, message) => {
    setBusy(true)
    setError('')
    try {
      await fn()
      notify(message)
      setDialog(null)
      setVersion((v) => v + 1)
    } catch (e) {
      setError(e.message || 'That did not work.')
      setDialog(null)
    } finally {
      setBusy(false)
    }
  }

  const name = monthName(ym)
  const closed = data?.closed
  const blockers = data ? data.drafts.length + data.draft_sales : 0
  // Plans without Inventory: the month's stock value must be in before it closes.
  const stockNeeded = !!data?.periodic_stock?.needed
  // A month is closed once it's over (the server refuses sooner): this
  // month, or a later one, says when it can be.
  const running = !!ym && ym >= thisYm()
  const closesFrom = ym ? monthName(thisYm(new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)), 1))) : ''

  return (
    <div className="ci-page">
      <AccountingTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Month-end close <HelpTip k="periodClose" /></h1>
          </div>
        </div>
        <div className="ci-header-actions">
          <select className="ci-select" value={ym || ''} onChange={(e) => choose(e.target.value)} aria-label="Month">
            {months.map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
          </select>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      {loading || !data ? <Spinner label="Checking the month…" /> : (
        <>
          <section className={`acc-close-status ${closed ? 'is-closed' : 'is-open'}`}>
            {closed ? <Lock size={18} aria-hidden="true" /> : <LockOpen size={18} aria-hidden="true" />}
            <div>
              <strong>{name} is {closed ? 'closed' : 'open'}</strong>
              <span>
                {closed
                  ? <>Closed {fmtDateTime(closed.closed_at)}{closed.closed_by ? ` by ${closed.closed_by}` : ''}.{closed.close_je_id && <> Closing entry <Link to={`/accounting/journal/${closed.close_je_id}`}>{docNo('je', closed.close_je_no)}</Link> <HelpTip k="closingEntry" /></>}</>
                  : running ? `It’s still running. You can close it from 1 ${closesFrom}.`
                    : blockers ? 'Post or delete the drafts below, then close it.'
                      : stockNeeded ? 'Enter the stock value below, then close it.' : 'Run through the checks, then close it.'}
              </span>
            </div>
            {closed
              ? <button data-write type="button" className="ci-btn ci-btn-ghost" onClick={() => setDialog('reopen')}><LockOpen size={14} />Reopen {monthName(ym, { year: false })}</button>
              : <button data-write type="button" className="ci-btn ci-btn-primary" disabled={blockers > 0 || running || stockNeeded} onClick={() => setDialog('close')}><Lock size={14} />Close {monthName(ym, { year: false })}</button>}
          </section>

          <ol className="acc-checks">
            <Check ok={blockers === 0} blocking title={blockers === 0 ? 'No drafts in the month' : `${blockers} ${blockers === 1 ? 'draft' : 'drafts'} dated in ${monthName(ym, { year: false })}`}
              help="checkDrafts">
              {data.drafts.length > 0 && (
                <ul className="acc-check-list">
                  {data.drafts.map((d) => (
                    <li key={d.je_id}><Link to={`/accounting/journal/${d.je_id}`}>{docNo('je', d.je_no)}</Link> {d.memo || 'No description'} · {fmtDay(d.date, { year: false })} · {money(d.amount)}</li>
                  ))}
                </ul>
              )}
              {data.draft_sales > 0 && <p className="acc-check-more">{data.draft_sales} draft {data.draft_sales === 1 ? 'sale' : 'sales'}: finish or void {data.draft_sales === 1 ? 'it' : 'them'} under <Link to="/sales">Sales</Link>.</p>}
            </Check>

            <Check ok={data.depreciation_due === 0 && data.prepaids_due === 0}
              title={data.depreciation_due === 0 && data.prepaids_due === 0 ? 'Depreciation and prepaids are up to date' : 'Depreciation or prepaids are due'}
              help="checkDue">
              {(data.depreciation_due > 0 || data.prepaids_due > 0) && (
                <ul className="acc-check-list">
                  {data.depreciation_due > 0 && <li><Link to="/accounting/assets">Depreciation due on {data.depreciation_due} {data.depreciation_due === 1 ? 'asset' : 'assets'}</Link></li>}
                  {data.prepaids_due > 0 && <li><Link to="/accounting/prepaids">{data.prepaids_due} {data.prepaids_due === 1 ? 'prepaid' : 'prepaids'} to amortize</Link></li>}
                </ul>
              )}
            </Check>

            {data.jobs_wip && has('jobs') && <JobsCheck ym={ym} wip={data.jobs_wip} closed={!!closed} onChange={() => setVersion((v) => v + 1)} />}
            {data.production_overhead && <ProductionCheck ym={ym} oh={data.production_overhead} closed={!!closed} onChange={() => setVersion((v) => v + 1)} />}

            <Check ok={data.bank.statements > 0 && data.bank.unmatched === 0} warn={data.bank.statements === 0}
              title={data.bank.statements === 0 ? 'No bank statements imported' : (data.bank.unmatched ? `${data.bank.unmatched} bank statement ${data.bank.unmatched === 1 ? 'line' : 'lines'} not matched` : 'Bank statement lines all matched')}
              help="checkBank">
              {(data.bank.statements === 0 || data.bank.unmatched > 0) && <p className="acc-check-more"><Link to="/accounting/bank-rec">{data.bank.statements === 0 ? 'Import a statement' : 'Match them'}</Link></p>}
            </Check>

            <TieOut title="Accounts payable" help="checkAp"
              account={`${data.ap.account_code} Accounts payable`} book={data.ap.gl_balance}
              detailLabel="Open vendor bills" detail={data.ap.open_bills} difference={data.ap.difference}
              link={{ to: '/purchase/ap-ledger', label: 'See what’s owed to each vendor' }} />

            {data.periodic_stock ? <StockValueCheck sv={data.periodic_stock} onChange={() => setVersion((v) => v + 1)} /> : (
              <>
                <TieOut title="Inventory" help="checkInventory"
                  account={`${data.inventory.account_code} Inventory`} book={data.inventory.gl_balance}
                  detailLabel="Stock value" detail={data.inventory.stock_value} difference={data.inventory.difference}
                  link={{ to: '/inventory/stock', label: 'Open stock' }} />

                <Check ok={!data.stock.negative_count && !data.stock.no_cost_count && !data.stock.odd_cost_count}
                  title={(!data.stock.negative_count && !data.stock.no_cost_count && !data.stock.odd_cost_count) ? 'No stock problems' : 'Stock needs a look'}
                  help="checkStock">
                  <StockList label="Below zero" help="negativeStock" items={data.stock.negative} count={data.stock.negative_count} show={(i) => `${i.qty.toLocaleString()} on hand`} />
                  <StockList label="No cost" help="noCost" items={data.stock.no_cost} count={data.stock.no_cost_count} show={(i) => `${i.qty.toLocaleString()} on hand`} />
                  <StockList label="Unusual cost" items={data.stock.odd_cost} count={data.stock.odd_cost_count} show={(i) => `average cost ${money(i.avg_cost)}`} />
                </Check>
              </>
            )}
          </ol>

          <section className="doc-card">
            <div className="doc-card-head"><h2>Closed months</h2><span>{data.history.length ? `${data.history.length} closed` : 'None yet'}</span></div>
            {data.history.length ? (
              <div className="doc-table-scroll">
                <table className="ci-line-table acc-history">
                  <thead><tr><th>Month</th><th>Closed</th><th>By</th><th>Closing entry</th></tr></thead>
                  <tbody>
                    {data.history.map((h) => (
                      <tr key={h.period_ym} className={h.period_ym === ym ? 'is-current' : undefined}>
                        <td><button type="button" className="acc-link-btn" onClick={() => choose(h.period_ym)}>{monthName(h.period_ym)}</button></td>
                        <td>{fmtDateTime(h.closed_at) || '–'}</td>
                        <td>{h.closed_by || '–'}</td>
                        <td>{h.close_je_id ? <Link to={`/accounting/journal/${h.close_je_id}`}>{docNo('je', h.close_je_no)}</Link> : 'Nothing to close'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="doc-empty">No months have been closed.</p>}
          </section>
        </>
      )}

      <Modal
        open={dialog === 'close'}
        onClose={() => setDialog(null)}
        title={`Close ${name}?`}
        subtitle="Nothing dated in the month can be posted, changed or voided afterwards."
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="primary" loading={busy} onClick={() => run(() => closePeriodWithBalances({ period_ym: ym, note: `Period close for ${ym}` }), `${name} closed`)}>Close {monthName(ym, { year: false })}</Button>
          </>
        )}
      >
        <p className="acc-modal-text">A closing entry dated the last day of the month moves its income and expenses into retained earnings (the profit the business has kept). Reports leave that entry out, so they still show what happened in {monthName(ym, { year: false })}. If something needs changing later, reopen the month.</p>
      </Modal>

      <Modal
        open={dialog === 'reopen'}
        onClose={() => setDialog(null)}
        title={`Reopen ${name}?`}
        subtitle="Entries dated in the month can be posted, changed and voided again."
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="danger" loading={busy} onClick={() => run(() => reopenPeriod({ period_ym: ym }), `${name} reopened`)}>Reopen</Button>
          </>
        )}
      >
        <p className="acc-modal-text">Its closing entry is voided, so closing the month again makes a new one from the month as it is then.</p>
      </Modal>
    </div>
  )
}

// Negative amounts as −$80.00 rather than $-80.00.
const signed = (v) => (Number(v) < 0 ? `−${money(-Number(v))}` : money(v))

// One check: a title with its "?", and detail only when there's something to do.
function Check({ ok, warn, blocking, title, help, children }) {
  const Icon = ok ? CheckCircle2 : (warn ? CircleDashed : AlertTriangle)
  return (
    <li className={`acc-check ${ok ? 'is-ok' : (blocking ? 'is-blocking' : 'is-warn')}`}>
      <Icon size={18} className="acc-check-icon" aria-hidden="true" />
      <div className="acc-check-body">
        <strong className="label-with-help">{title}{help && <HelpTip k={help} />}</strong>
        {children}
      </div>
    </li>
  )
}

/** Long jobs: post (or undo) the month's adjustment, so their income is
    what the work earned rather than what was invoiced. */
function JobsCheck({ ym, wip, closed, onChange }) {
  const { notify } = useToast()
  const [working, setWorking] = useState(false)
  const posted = !!wip.run
  const act = async (fn, done) => {
    setWorking(true)
    try {
      await fn(ym)
      notify(done)
      onChange()
    } catch (e) {
      notify(e.message || 'That didn’t work.')
    } finally {
      setWorking(false)
    }
  }
  const title = posted
    ? `Jobs in progress adjusted for ${monthName(ym, { year: false })}`
    : (wip.to_adjust ? `${wip.to_adjust} long ${wip.to_adjust === 1 ? 'job needs' : 'jobs need'} the month-end adjustment` : 'Long jobs need no adjustment')
  return (
    <Check ok={posted || !wip.to_adjust} title={title}
      help={undefined}>
      <p className="acc-check-more">
        Long jobs count income as the work is done. This books the difference between what each has earned and what’s been billed, on the month’s last day, and reverses it on the 1st.{' '}
        <HelpTip text="Earned more than billed: a contract asset (work done, not yet invoiced). Billed more than earned: a contract liability (invoiced ahead of the work). A job heading for a loss books the whole loss now." />
      </p>
      {wip.problems.length > 0 && (
        <ul className="acc-check-list">
          {wip.problems.map((p) => <li key={p.job_id}><Link to={`/jobs/${p.job_id}`}>{p.label}</Link> · {p.problem}</li>)}
        </ul>
      )}
      <p className="acc-check-more">
        <Link to={`/accounting/reports/jobs-in-progress?as_of=${ym}-${String(new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)), 0).getDate()).padStart(2, '0')}`}>See jobs in progress</Link>
        {!closed && !posted && wip.to_adjust > 0 && <span data-write> · <button type="button" className="ci-link-btn" disabled={working} onClick={() => act(postJobsWip, 'Jobs adjustment posted')}>{working ? 'Posting…' : 'Post the adjustment'}</button></span>}
        {!closed && posted && <> · Posted{wip.run.posted_by ? ` by ${wip.run.posted_by}` : ''} for {wip.run.job_count} {wip.run.job_count === 1 ? 'job' : 'jobs'}<span data-write> · <button type="button" className="ci-link-btn" disabled={working} onClick={() => act(undoJobsWip, 'Jobs adjustment undone')}>{working ? 'Undoing…' : 'Undo'}</button></span></>}
      </p>
    </Check>
  )
}

/** Production: factory labour and overhead still in their accounts at
    month-end weren't assigned to any run. Labour moves to overhead
    (indirect labour), then overhead to cost of goods sold. */
function ProductionCheck({ ym, oh, closed, onChange }) {
  const { notify } = useToast()
  const { readOnly } = useSession()
  const [working, setWorking] = useState(false)
  const left = Math.abs(oh.labour) >= 0.005 || Math.abs(oh.overhead) >= 0.005
  const act = async (fn, done) => {
    setWorking(true)
    try {
      await fn(ym)
      notify(done)
      onChange()
    } catch (e) {
      notify(e.message || 'That didn’t work.')
    } finally {
      setWorking(false)
    }
  }
  const month = monthName(ym, { year: false })
  const title = left
    ? `${signed(oh.to_cogs)} of factory labour and overhead not assigned to a run`
    : `Factory labour and overhead cleared for ${month}`
  return (
    <Check ok={!left} title={title}>
      {left && (
        <dl className="acc-tieout">
          <div><dt>{oh.accounts.factory_labour} Factory labour</dt><dd>{signed(oh.labour)}</dd></div>
          <div><dt>{oh.accounts.manufacturing_overhead} Manufacturing overhead</dt><dd>{signed(oh.overhead)}</dd></div>
          <div className="is-diff"><dt>To cost of goods sold</dt><dd>{signed(oh.to_cogs)}</dd></div>
        </dl>
      )}
      {left && (
        <p className="acc-check-more">
          On the month’s last day, labour not on a run moves to manufacturing overhead (indirect labour), then overhead not assigned to a run goes to cost of goods sold.{' '}
          <HelpTip text="Labour and overhead are recorded in their own accounts, then moved into production runs. What's left at month-end wasn't used on any run: under-applied overhead, which GAAP sends to cost of goods sold. A negative amount is over-applied: more was moved into runs than was recorded, so cost of goods sold goes down." />
        </p>
      )}
      <p className="acc-check-more">
        {[
          ...oh.posted.map((p) => <Link key={p.je_id} to={`/accounting/journal/${p.je_id}`}>{docNo('je', p.je_no)}</Link>),
          !closed && left && !readOnly && <button key="post" type="button" className="ci-link-btn" disabled={working} onClick={() => act(postProductionOverhead, 'Moved to cost of goods sold')}>{working ? 'Posting…' : 'Move to cost of goods sold'}</button>,
          !closed && oh.posted.length > 0 && !readOnly && <button key="undo" type="button" className="ci-link-btn" disabled={working} onClick={() => act(undoProductionOverhead, 'Undone')}>{working ? 'Undoing…' : 'Undo'}</button>,
        ].filter(Boolean).flatMap((el, i) => (i ? [<span key={`s${i}`}> · </span>, el] : [el]))}
      </p>
    </Check>
  )
}

/** Plans without Inventory: the stock left at the month's end, valued at
    cost; what was used or sold moves to cost of goods sold (periodic method). */
function StockValueCheck({ sv, onChange }) {
  const month = monthName(sv.period_ym, { year: false })
  const title = sv.entered && !sv.out_of_date
    ? `Stock at the end of ${month}: ${money(sv.entered.value)}`
    : (sv.out_of_date ? `Enter ${month}’s stock value again` : (sv.needed ? `What’s your stock worth at the end of ${month}?` : 'No stock on the books to value'))
  return (
    <Check ok={!sv.needed} blocking={sv.needed} title={title}>
      <p className="acc-check-more">
        Your plan doesn’t count stock as it moves. Count what’s left, enter its value at cost, and what was used or sold goes to cost of goods sold on the month’s last day.{' '}
        <HelpTip text="Goods you receive are kept in Inventory, an asset, until they're used or sold. Without stock counts in ASYS that happens at month end, from what you count (the periodic method). Counting more than the books hold, such as goods bought before this, moves the difference back off cost of goods sold." />
      </p>
      <StockValueForm data={sv} onSaved={onChange} />
      <p className="acc-check-more"><Link to={`/purchase/stock-value?month=${sv.period_ym}`}>Stock values by month</Link></p>
    </Check>
  )
}

function TieOut({ title, help, account, book, detailLabel, detail, difference, link }) {
  const ok = Math.abs(Number(difference || 0)) < 0.005
  return (
    <Check ok={ok} help={help} title={ok ? `${title} ties out` : `${title} is out by ${money(Math.abs(difference))}`}>
      {!ok && (
        <>
          <dl className="acc-tieout">
            <div><dt>{account}</dt><dd>{signed(book)}</dd></div>
            <div><dt>{detailLabel}</dt><dd>{signed(detail)}</dd></div>
            <div className="is-diff"><dt>Difference</dt><dd>{signed(difference)}</dd></div>
          </dl>
          <p className="acc-check-more"><Link to={link.to}>{link.label}</Link></p>
        </>
      )}
    </Check>
  )
}

function StockList({ label, help, items, count, show }) {
  if (!count) return null
  return (
    <div className="acc-stock">
      <span className="acc-stock-label label-with-help">{label}: {count}<HelpTip k={help} /></span>
      <ul className="acc-check-list">
        {items.map((i) => <li key={i.product_id}><Link to={`/inventory/stock/${i.product_id}`}>{i.name || i.sku}</Link> · {show(i)}</li>)}
        {count > items.length && <li>…and {count - items.length} more</li>}
      </ul>
    </div>
  )
}
